import React from 'react';
import { Area, AreaChart, XAxis } from 'recharts';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { useChartDateFormatters } from '@/lib/useChartDateFormatters';

export const description = 'A linear area chart';

export interface ChartAreaLinearProps {
  title: string;
  metric: string;
  data: {
    date: Date | string;
    key1: number;
    key2?: number;
    key3?: number;
    key4?: number;
    key5?: number;
  }[];
  chartConfig: ChartConfig;
  locale?: string;
}

export function ChartAreaLinear({ title, metric, data, chartConfig, locale }: ChartAreaLinearProps) {
  const { tickFormatter, formatXLabel } = useChartDateFormatters(locale);

  return (
    <Card className="min-w-80 h-44 p-4 flex flex-col text-dark shadow-none">
      <CardHeader className="p-0 space-y-0" data-name="header">
        <CardTitle className="uppercase font-inter heading-xxxs tracking-normal">{title}</CardTitle>
        <CardDescription data-name="metric" className="mt-0 text-[2rem] leading-10 text-dark font-inter">
          {metric}
        </CardDescription>
      </CardHeader>
      <CardContent data-name="content" className="p-0 flex-1 min-h-0">
        <ChartContainer config={chartConfig} className="h-full w-full">
          <AreaChart
            accessibilityLayer
            data={data}
            margin={{
              top: 2,
              left: 14,
              right: 23,
            }}
          >
            <defs>
              <linearGradient id="key1Gradient" x1="0" y1="0" x2="0" y2="1">
                <stop stopColor="var(--color-key1)" />
                <stop offset="1" stopColor="var(--color-key1)" stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis
              dataKey="date"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              minTickGap={20}
              interval="preserveStartEnd"
              tickFormatter={tickFormatter}
              tick={({ x, y, payload }) => (
                <text
                  x={x}
                  y={y}
                  dy={12}
                  textAnchor="middle"
                  className="uppercase font-bold font-inter tracking-tight !fill-dark-900"
                >
                  {tickFormatter(payload.value)}
                </text>
              )}
            />
            <ChartTooltip
              cursor={false}
              content={({ active, payload, label }) => (
                <ChartTooltipContent active={active} payload={payload} hideIndicator label={formatXLabel(label)} />
              )}
            />
            <Area
              dataKey="key1"
              type="linear"
              fill="url(#key1Gradient)"
              stroke="var(--color-key1)"
              strokeLinecap="round"
              strokeWidth={3}
            />
          </AreaChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}
